import React, { useEffect, useState } from 'react';
import { Table, Tag, Typography, message, Empty } from 'antd';
import { useFetchWrapper } from '_helpers';

const { Text } = Typography;

export { TeacherKpiPage };

// KPI colour bands — green ≥70, orange ≥40, red <40 (matches the attendance palette).
const kpiColor = (kpi) => (kpi >= 70 ? '#4CAF6E' : kpi >= 40 ? '#E8A04C' : '#D95C5C');

// Homework-distribution summary tags (compact). Only non-zero buckets shown.
const HW_BUCKETS = [
    { key: 'excellent',  label: 'XS',    color: '#4CAF6E' },
    { key: 'complete',   label: 'Đủ',    color: '#52B6CE' },
    { key: 'incomplete', label: 'Thiếu', color: '#E8A04C' },
    { key: 'none',       label: 'Ko',    color: '#D95C5C' },
];

function TeacherKpiPage() {
    const fetchWrapper = useFetchWrapper();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const res = await fetchWrapper.get('/api/manager/teachers/kpi', null, null);
                const json = await res.json();
                if (json.status === 'Success') setRows(Array.isArray(json.message) ? json.message : []);
                else message.error(json.message || 'Không tải được dữ liệu KPI');
            } catch (e) {
                message.error('Không tải được dữ liệu KPI');
            }
            setLoading(false);
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const columns = [
        {
            title: 'Tên',
            key: 'name',
            width: 200,
            render: (_, r) => (
                <div>
                    <div style={{ fontWeight: 600 }}>{r.name || '—'}</div>
                    <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>{r.student_id}</div>
                </div>
            ),
        },
        {
            title: 'KPI',
            key: 'kpi',
            dataIndex: 'kpi',
            width: 110,
            align: 'center',
            defaultSortOrder: 'descend',
            // nulls last in both directions
            sorter: (a, b) => (a.kpi == null ? -1 : a.kpi) - (b.kpi == null ? -1 : b.kpi),
            render: (kpi) => kpi == null
                ? <Text type="secondary">—</Text>
                : <span style={{ fontSize: 22, fontWeight: 700, color: kpiColor(kpi) }}>{kpi}</span>,
        },
        {
            title: 'Hoàn thành điểm danh',
            key: 'attendance_pct',
            width: 200,
            align: 'center',
            render: (_, r) => {
                if (r.attendance_pct == null) return <Text type="secondary">Chưa có dữ liệu</Text>;
                const base = (r.expected || 0) - (r.cancelled || 0);
                return (
                    <div>
                        <div style={{ fontWeight: 600 }}>{r.attendance_pct}%</div>
                        <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>
                            {r.completed}/{base} buổi
                            {r.cancelled > 0 ? ` · ${r.cancelled} hủy` : ''}
                        </div>
                    </div>
                );
            },
        },
        {
            title: 'Điểm buổi học TB',
            key: 'avg_rating',
            width: 140,
            align: 'center',
            sorter: (a, b) => (a.avg_rating == null ? -1 : a.avg_rating) - (b.avg_rating == null ? -1 : b.avg_rating),
            render: (_, r) => r.avg_rating == null
                ? <Text type="secondary">—</Text>
                : <span style={{ fontWeight: 600 }}>{r.avg_rating.toFixed(1)}</span>,
        },
        {
            title: 'Đúng giờ',
            key: 'punctuality_pct',
            width: 150,
            align: 'center',
            sorter: (a, b) => (a.punctuality_pct == null ? -1 : a.punctuality_pct) - (b.punctuality_pct == null ? -1 : b.punctuality_pct),
            render: (_, r) => {
                if (r.punctuality_pct == null) return <Text type="secondary">Chưa có dữ liệu</Text>;
                return (
                    <div>
                        <div style={{ fontWeight: 600 }}>{r.punctuality_pct}%</div>
                        <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>{r.on_time}/{r.held} buổi</div>
                    </div>
                );
            },
        },
        {
            // Volume, not quality — deliberately outside the KPI score. Counts every session the
            // teacher checked in to, lớp học thử included (those count here and nowhere else).
            title: 'Tổng giờ dạy',
            key: 'teachtime_min',
            width: 130,
            align: 'center',
            sorter: (a, b) => (a.teachtime_min || 0) - (b.teachtime_min || 0),
            render: (_, r) => {
                const min = r.teachtime_min || 0;
                if (!min) return <Text type="secondary">—</Text>;
                return (
                    <div>
                        <div style={{ fontWeight: 600 }}>{(min / 60).toFixed(1)} giờ</div>
                        <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>{min} phút</div>
                    </div>
                );
            },
        },
        {
            title: 'Kết quả BTVN',
            key: 'homework_dist',
            width: 220,
            render: (_, r) => {
                const dist = r.homework_dist || {};
                const tags = HW_BUCKETS
                    .filter(b => (dist[b.key] || 0) > 0)
                    .map(b => (
                        <Tag key={b.key} color={b.color} style={{ margin: '2px 4px 2px 0' }}>
                            {b.label}:{dist[b.key]}
                        </Tag>
                    ));
                return tags.length ? <span>{tags}</span> : <Text type="secondary">—</Text>;
            },
        },
        {
            title: 'Số lớp',
            key: 'classes',
            width: 90,
            align: 'center',
            sorter: (a, b) => (a.classes?.length || 0) - (b.classes?.length || 0),
            render: (_, r) => (r.classes?.length || 0),
        },
    ];

    return (
        <div className="att2-page soft-in">
            <div className="att2-head">
                <div className="att2-head-l">
                    <h1 className="att2-title">KPI Giáo viên</h1>
                </div>
            </div>
            <div className="float-panel soft-in-scale">
                <Table
                    columns={columns}
                    dataSource={rows}
                    rowKey="teacher_id"
                    loading={loading}
                    pagination={false}
                    scroll={{ x: 1050 }}
                    locale={{ emptyText: <Empty description="Chưa có dữ liệu" /> }}
                />
            </div>
        </div>
    );
}
